import type * as Monaco from "monaco-editor";

/**
 * Token/diff colors shared by the SQL editor and the read-only SQL code block.
 * Split by theme so the Monarch themes below and the editor's diff view-zone
 * styling read from one source of truth.
 */
export const THEME_COLORS = {
  dark: {
    transparent: "#00000000",
    gray: "#525252",
    blue: "9CDCFE",
    yellow: "DCDCAA",
    teal: "4EC9B0",
    greenText: "#7bf1a8", // green-300
    greenBgSubtle: "#032e15", // green-950
    greenBg: "#0d542b", // green-900
    greenBgHover: "#016630", // green-800
    redText: "#ffa2a2", // red-300
    redBgSubtle: "#460809", // red-950
    redBg: "#82181a", // red-900
    redBgHover: "#82181a", // red-900
    neutralBg: "#171717", // neutral-900
    neutralBgHover: "#262626", // neutral-800
    neutralText: "#d4d4d4", // neutral-300
  },
  light: {
    transparent: "#ffffff00",
    gray: "#a3a3a3",
    blue: "001080",
    yellow: "795E26",
    teal: "267F99",
    greenText: "rgb(1, 102, 48)", // green-800
    greenBgSubtle: "rgb(240, 253, 244)", // green-50
    greenBg: "rgb(220, 252, 231)", // green-100
    greenBgHover: "rgb(185, 248, 207)", // green-200
    redText: "#9f0712", // red-800
    redBgSubtle: "#fef2f2", // red-50
    redBg: "ffe2e2", // red-100
    redBgHover: "#ffc9c9", // red-200
    neutralBg: "#f5f5f5", // neutral-100
    neutralBgHover: "#e5e5e5", // neutral-200
    neutralText: "#262626", // neutral-800
  },
} as const;

let registered = false;

/**
 * Registers the shared SQL Monarch tokenizer and the `index-dark` /
 * `index-light` editor themes on a Monaco instance. Idempotent — safe to call
 * from every {@link SQLEditor} mount and every read-only SQL code block so both
 * surfaces tokenize and color SQL identically.
 */
export const setupSqlMonaco = (monacoInstance: typeof Monaco) => {
  if (registered) return;
  registered = true;

  monacoInstance.languages.setMonarchTokensProvider("sql", {
    defaultToken: "",
    tokenPostfix: ".sql",
    ignoreCase: true,
    brackets: [
      { open: "[", close: "]", token: "delimiter.square" },
      { open: "(", close: ")", token: "delimiter.parenthesis" },
    ],
    keywords: [
      "SELECT",
      "FROM",
      "WHERE",
      "AND",
      "OR",
      "NOT",
      "IN",
      "LIKE",
      "BETWEEN",
      "IS",
      "NULL",
      "ORDER",
      "BY",
      "GROUP",
      "HAVING",
      "LIMIT",
      "OFFSET",
      "JOIN",
      "LEFT",
      "RIGHT",
      "INNER",
      "OUTER",
      "FULL",
      "CROSS",
      "ON",
      "AS",
      "DISTINCT",
      "INSERT",
      "INTO",
      "VALUES",
      "UPDATE",
      "SET",
      "DELETE",
      "CREATE",
      "ALTER",
      "DROP",
      "TABLE",
      "UNION",
      "ALL",
      "EXCEPT",
      "INTERSECT",
      "WITH",
      "ASC",
      "DESC",
      "NULLS",
      "FIRST",
      "LAST",
      "TRUE",
      "FALSE",
      "EXISTS",
      "ANY",
      "SOME",
      "CASE",
      "WHEN",
      "THEN",
      "ELSE",
      "END",
      "CAST",
      "OVER",
      "PARTITION",
      "ROWS",
      "RANGE",
      "PRECEDING",
      "FOLLOWING",
      "UNBOUNDED",
      "CURRENT",
      "ROW",
      "WINDOW",
      "UNNEST",
      "STRUCT",
      "ARRAY",
      "SAFE_CAST",
      "IF",
      "IFNULL",
      "COALESCE",
      "NULLIF",
      "EXTRACT",
      "DATE",
      "TIMESTAMP",
      "INTERVAL",
      "RECURSIVE",
      "LATERAL",
      "TABLESAMPLE",
      "PIVOT",
      "UNPIVOT",
      "QUALIFY",
    ],
    builtinFunctions: [
      "COUNT",
      "SUM",
      "AVG",
      "MIN",
      "MAX",
      "ABS",
      "CEIL",
      "FLOOR",
      "ROUND",
      "TRUNC",
      "MOD",
      "SIGN",
      "SQRT",
      "POW",
      "POWER",
      "LOG",
      "LN",
      "EXP",
      "LENGTH",
      "LOWER",
      "UPPER",
      "TRIM",
      "LTRIM",
      "RTRIM",
      "SUBSTR",
      "SUBSTRING",
      "REPLACE",
      "CONCAT",
      "SPLIT",
      "STARTS_WITH",
      "ENDS_WITH",
      "CONTAINS_SUBSTR",
      "REGEXP_CONTAINS",
      "REGEXP_EXTRACT",
      "REGEXP_REPLACE",
      "FORMAT",
      "LPAD",
      "RPAD",
      "REVERSE",
      "REPEAT",
      "CURRENT_DATE",
      "CURRENT_TIMESTAMP",
      "DATE_ADD",
      "DATE_SUB",
      "DATE_DIFF",
      "DATE_TRUNC",
      "TIMESTAMP_ADD",
      "TIMESTAMP_SUB",
      "TIMESTAMP_DIFF",
      "TIMESTAMP_TRUNC",
      "FORMAT_DATE",
      "FORMAT_TIMESTAMP",
      "PARSE_DATE",
      "PARSE_TIMESTAMP",
      "GENERATE_UUID",
      "ROW_NUMBER",
      "RANK",
      "DENSE_RANK",
      "NTILE",
      "LAG",
      "LEAD",
      "FIRST_VALUE",
      "LAST_VALUE",
      "NTH_VALUE",
      "ARRAY_AGG",
      "STRING_AGG",
      "COUNTIF",
      "LOGICAL_AND",
      "LOGICAL_OR",
      "APPROX_COUNT_DISTINCT",
    ],
    typeKeywords: [
      "INT64",
      "INT",
      "SMALLINT",
      "INTEGER",
      "BIGINT",
      "TINYINT",
      "BYTEINT",
      "FLOAT64",
      "NUMERIC",
      "BIGNUMERIC",
      "DECIMAL",
      "BIGDECIMAL",
      "BOOL",
      "BOOLEAN",
      "STRING",
      "BYTES",
      "DATE",
      "DATETIME",
      "TIME",
      "TIMESTAMP",
      "GEOGRAPHY",
      "JSON",
    ],
    operators: [
      "=",
      ">",
      "<",
      "!",
      "~",
      "<=",
      ">=",
      "!=",
      "<>",
      "+",
      "-",
      "*",
      "/",
      "%",
      "||",
      ">>",
      "<<",
    ],
    tokenizer: {
      root: [
        [/`[^`]*`/, "identifier.quote"],
        [/--.*$/, "comment"],
        [/\/\*/, "comment", "@comment"],
        [/'/, "string", "@singleString"],
        [/"/, "string.double", "@doubleString"],
        [/0[xX][0-9a-fA-F]+/, "number.hex"],
        [/\d+(\.\d+)?([eE][-+]?\d+)?/, "number"],
        [/[;,.]/, "delimiter"],
        [/[()]/, "@brackets"],
        [/\[/, "@brackets"],
        [/]/, "@brackets"],
        [
          /[a-zA-Z_]\w*/,
          {
            cases: {
              "@keywords": "keyword",
              "@builtinFunctions": "predefined",
              "@typeKeywords": "type",
              "@default": "identifier",
            },
          },
        ],
        [/[<>=!~+\-*/%|&^]+/, "operator"],
        [/\s+/, "white"],
      ],
      comment: [
        [/[^/*]+/, "comment"],
        [/\*\//, "comment", "@pop"],
        [/[/*]/, "comment"],
      ],
      singleString: [
        [/[^']+/, "string"],
        [/''/, "string.escape"],
        [/'/, "string", "@pop"],
      ],
      doubleString: [
        [/[^"]+/, "string.double"],
        [/""/, "string.escape"],
        [/"/, "string.double", "@pop"],
      ],
    },
  });

  const dark = THEME_COLORS.dark;
  const light = THEME_COLORS.light;

  monacoInstance.editor.defineTheme("index-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "identifier.quote", foreground: dark.blue },
      { token: "predefined", foreground: dark.yellow },
      { token: "type", foreground: dark.teal },
    ],
    colors: {
      "editor.background": dark.transparent,
      "editorLineNumber.foreground": dark.gray,
    },
  });

  monacoInstance.editor.defineTheme("index-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "identifier.quote", foreground: light.blue },
      { token: "predefined", foreground: light.yellow },
      { token: "type", foreground: light.teal },
    ],
    colors: {
      "editor.background": light.transparent,
      "editorLineNumber.foreground": light.gray,
    },
  });
};
